import { useEffect, useRef, useState } from "react";
import { useT } from "../../lib/use-t";

export type SessionMode = "full" | "deel-1" | "deel-2" | "deel-3" | "deel-4";

/**
 * Visual progress while /api/exam/start runs. The endpoint doesn't stream
 * progress, so this is a smooth time-based animation that:
 *   - fills toward 95% over the mode's expected duration,
 *   - eases asymptotically past 95% if the call runs longer,
 *   - rotates a status message every few seconds so the wait feels
 *     informed rather than indefinite,
 *   - snaps to 100% the moment `done` flips true (caller signals).
 *
 * Per-mode duration estimates from real measurements:
 *   - full: 8s — Deel 1 has no photos, just question text + TTS
 *   - deel-1: 6s — same shape, fewer turns to generate
 *   - deel-2: 10s — 1 photo + TTS in parallel
 *   - deel-3: 13s — 2 photos + TTS in parallel
 *   - deel-4: 16s — 3 photos + TTS in parallel
 */

const EXPECTED_MS: Record<SessionMode, number> = {
  full: 8_000,
  "deel-1": 6_000,
  "deel-2": 10_000,
  "deel-3": 13_000,
  "deel-4": 16_000,
};

type Stage = {
  /** When (as fraction of expected) this message starts showing. */
  at: number;
  textKey: string;
};

function stagesFor(mode: SessionMode): Stage[] {
  const noPhotos = mode === "full" || mode === "deel-1";
  return noPhotos
    ? [
        { at: 0, textKey: "prepProgress.writingQuestions" },
        { at: 0.5, textKey: "prepProgress.preparingAudio" },
        { at: 0.95, textKey: "prepProgress.finishing" },
      ]
    : [
        { at: 0, textKey: "prepProgress.writingQuestions" },
        { at: 0.3, textKey: "prepProgress.drawingPhotos" },
        { at: 0.7, textKey: "prepProgress.preparingAudio" },
        { at: 0.95, textKey: "prepProgress.finishing" },
      ];
}

export function PrepProgress({ mode, done }: { mode: SessionMode; done: boolean }) {
  const t = useT();
  const expected = EXPECTED_MS[mode];
  const stages = stagesFor(mode);
  const startedAt = useRef<number>(performance.now());
  const [pct, setPct] = useState(0);
  const [stageIdx, setStageIdx] = useState(0);

  useEffect(() => {
    if (done) {
      setPct(100);
      return;
    }
    let raf = 0;
    function tick() {
      const elapsed = performance.now() - startedAt.current;
      const linear = elapsed / expected; // 0..1 over the expected duration
      // Cap at 95% before `done`. Past 1.0 we ease asymptotically toward
      // 95%, so a slow call still feels alive without ever reaching the
      // end without a real response.
      let target: number;
      if (linear <= 0.95) {
        target = linear * 100; // 0 → 95
      } else {
        // Once past expected, walk slowly: each extra `expected/4` ms
        // closes 25% of the remaining gap to 95.
        const overrun = (linear - 0.95) / 0.25;
        target = 95 - 95 * Math.exp(-overrun) + 0; // approaches 95
      }
      setPct(Math.min(95, target));
      // Pick the latest stage whose `at` threshold has been crossed.
      const cur = Math.min(linear, 1);
      let next = 0;
      for (let i = 0; i < stages.length; i++) {
        const s = stages[i];
        if (s && cur >= s.at) next = i;
      }
      setStageIdx(next);
      raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [done, expected, stages]);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy={!done}
      className="space-y-3 max-w-sm"
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-serif-italic text-base text-[var(--ink-muted)]">
          {done ? t("prepProgress.done") : (stages[stageIdx] ? t(stages[stageIdx].textKey) : t("modePicker.preparingEllipsis"))}
        </span>
        <span className="font-mono-badge text-xs text-[var(--ink-faint)] tabular-nums">
          {Math.round(pct)}%
        </span>
      </div>
      <div
        className="h-1 w-full rounded-full bg-[var(--panel-soft)] overflow-hidden"
        aria-hidden
      >
        <div
          className="h-full rounded-full bg-[var(--accent)] transition-[width] duration-200 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
